import {
  Box,
  Typography,
  Button,
  Stack,
  InputAdornment,
  FormHelperText,
} from "@mui/material";
import { CustomInput } from "global/components";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { useState, useEffect, useRef } from "react";
import { addSender, updateSender } from "../SendersService";
import strings from "global/constants/StringConstants";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  filters: any;
  setFilters: Function;
  onSenderAdded: (
    senderId: number,
    senderName: string,
    senderEmail: string
  ) => void;
  isEdit?: boolean;
  editSenderData?: {
    id: number;
    name: string;
    email: string;
  };
}

const AddUpdateSender = (props: CustomProps) => {
  const {
    isVisible,
    handleDialogClose,
    filters,
    setFilters,
    onSenderAdded,
    isEdit = false,
    editSenderData,
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDataModified, setIsDataModified] = useState<boolean>(false);
  const [isEmailChanged, setIsEmailChanged] = useState<boolean>(false);
  const originalDataRef = useRef<{ name: string; email: string } | null>(null);
  const [fields, setFields] = useState({
    id: {
      value: 0,
      error: "",
    },
    name: {
      value: "",
      error: "",
    },
    email: {
      value: "",
      error: "",
    },
  });

  useEffect(() => {
    if (isEdit && editSenderData) {
      setFields({
        id: {
          value: editSenderData.id,
          error: "",
        },
        name: {
          value: editSenderData.name,
          error: "",
        },
        email: {
          value: editSenderData.email,
          error: "",
        },
      });

      originalDataRef.current = {
        name: editSenderData.name,
        email: editSenderData.email,
      };

      setIsDataModified(false);
      setIsEmailChanged(false);
    } else {
      setFields({
        id: {
          value: 0,
          error: "",
        },
        name: {
          value: "",
          error: "",
        },
        email: {
          value: "",
          error: "",
        },
      });

      setIsDataModified(true);
      originalDataRef.current = null;
    }
  }, [isEdit, editSenderData, isVisible]);

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;

    setFields({
      ...fields,
      [name]: {
        value: value,
        error: "",
      },
    });

    if (isEdit && originalDataRef.current) {
      const nameChanged =
        name === "name" && value.trim() !== originalDataRef.current.name;
      const emailChanged =
        name === "email" && value.trim() !== originalDataRef.current.email;

      if (emailChanged) {
        setIsEmailChanged(true);
      } else if (
        name === "email" &&
        value.trim() === originalDataRef.current.email
      ) {
        setIsEmailChanged(false);
      }

      const otherFieldChanged =
        (name === "name" &&
          fields.email.value.trim() !== originalDataRef.current.email) ||
        (name === "email" &&
          fields.name.value.trim() !== originalDataRef.current.name);

      setIsDataModified(nameChanged || emailChanged || otherFieldChanged);
    }
  };

  const handleValidation = () => {
    let isValid = true;
    const senderName = fields.name.value.trim();
    const senderEmail = fields.email.value.trim();
    const errors = {
      ...fields,
      name: { ...fields.name },
      email: { ...fields.email },
    };

    if (!senderName) {
      errors.name.error = "Please enter sender name";
      isValid = false;
    }
    if (!senderEmail) {
      errors.email.error = "Please enter sender email";
      isValid = false;
    } else if (!strings.regex.test(senderEmail)) {
      errors.email.error = "Please enter valid email";
      isValid = false;
    }
    setFields(errors);
    return isValid;
  };

  const handleClose = () => {
    handleDialogClose();
  };

  const addUpdateSenderHandler = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const body = {
          id: isEdit ? fields.id.value : 0,
          name: fields.name.value.trim(),
          email: fields.email.value.trim(),
        };

        if (isEdit && editSenderData) {
          await updateSender(body);
          openSuccessNotification("Sender updated successfully");
          setFilters({
            ...filters,
            page: 1,
          });
          handleClose();
          if (isEmailChanged) {
            handleClose();
            onSenderAdded(
              fields.id.value,
              fields.name.value.trim(),
              fields.email.value.trim()
            );
          }
        } else {
          const res = await addSender(body);
          openSuccessNotification("Sender added successfully");
          setFilters({
            ...filters,
            page: 1,
          });
          handleClose();
          onSenderAdded(res, body.name, body.email);
        }
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getDialogBody = () => {
    return (
      <Stack spacing={3}>
        <Stack spacing={2}>
          <Box>
            <CustomInput
              required
              id="name-input"
              label="Sender Name"
              placeHolder="Sender name"
              name="name"
              value={fields.name.value}
              onChange={handleOnChange}
              propsToInputElement={{ minLength: 1, maxLength: 50 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {fields.name.value?.length} / 50
                    </span>
                  </InputAdornment>
                ),
              }}
              autoComplete="new-name"
              error={!isTruthy(fields.name.value) && fields.name.error}
              helperText={!isTruthy(fields.name.value) ? fields.name.error : ""}
            />
          </Box>
          <Box>
            <CustomInput
              required
              id="email"
              label="Email"
              placeHolder="Sender email"
              name="email"
              disabled={isEdit}
              value={fields.email.value}
              onChange={handleOnChange}
              propsToInputElement={{ minLength: 1, maxLength: 50 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ fontSize: "0.75rem" }}>
                      {fields.email.value?.length} / 50
                    </span>
                  </InputAdornment>
                ),
              }}
              error={isTruthy(fields.email.error) && fields.email.error}
              helperText={
                isTruthy(fields.email.error) ? fields.email.error : ""
              }
              autoComplete="new-email"
            />
          </Box>
        </Stack>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => addUpdateSenderHandler()}
          disabled={isEdit && !isDataModified}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Save
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        handleDialogClose={() => handleClose()}
        isDialogOpen={isVisible}
        body={getDialogBody()}
        footer={getFooter()}
        title={
          <Typography variant="h4" color="#7551D0">
            {isEdit ? "Edit sender" : "Add sender"}
          </Typography>
        }
        closeButtonVisibility
        closable
        maxWidth="sm"
        persistOnClickOutside
        persistOnEsc
      />
    </>
  );
};

export default AddUpdateSender;
